import {
  get,
  hasIn,
  map,
  filter,
  sortBy,
  groupBy,
  memoize,
  includes,
  split,
  isEmpty,
} from 'lodash';

import { MEDIA_TYPES } from 'constants/media';
import { N_THUMBNAILS_PER_SLIDE, EDITIONS_PRIORITY, MSRP_PRICE_TIER } from 'constants/pdp';
import {
  CAMPAIGN_NAMES,
  PERSONALIZATION_STATUS,
  CAMPAIGN_IDENTIFIERS,
} from 'constants/personalizations';
import { SHOP_IDS } from 'constants/shops';
import BooleanExperimentCampaign from 'models/BooleanExperimentCampaign';
import Product from 'models/Product';
import Variant from 'models/Variant';
import { MODAL_TYPES } from 'state/constants';
import PersonalizationsSelector from 'state/selectors/personalizations';
import Cloudinary from 'utils/Cloudinary';
import Log from 'utils/Log';
import Settings from 'utils/Settings';
const cacheKey = (...args) => JSON.stringify(args);

const getCurrentProductHandle = state => get(state, ['pdp', 'currentProduct']);

const getInitialProductHandle = state => get(state, ['pdp', 'initialProduct']);

const getCurrentProduct = state =>
  new Product(get(state, ['pdp', 'products', getCurrentProductHandle(state)]));

const getCurrentOpenAttributeIndex = state => get(state, ['pdp', 'currentOpenAttributeIndex']);

const getCurrentSize = state => {
  const currentSize = get(state, ['pdp', 'currentSize']);
  const sizeIsValid = hasIn(getCurrentProduct(state), ['sizes', currentSize]);

  if (!sizeIsValid) {
    return null;
  }

  return currentSize;
};

const getSelectedQuantity = state => get(state, ['pdp', 'selectedQuantity'], 1);

const getAllProductHandles = state => get(state, ['pdp', 'masterProduct', 'products']);

const getAllProducts = state =>
  map(getAllProductHandles(state), handle => new Product(get(state, ['pdp', 'products', handle])));

const getIsLoadingMasterProduct = state => !get(state, ['pdp', 'masterProduct']);

const getAdditionalPdpModules = state => get(state, ['pdp', 'modules']);

const getCurrentVariant = state => {
  const sizeData = get(getCurrentProduct(state), ['sizes', getCurrentSize(state)]);
  if (!sizeData) {
    return undefined;
  }
  return new Variant(sizeData);
};

const getCurrentSku = state => get(getCurrentVariant(state), ['sku']);

const getCurrentId = state => get(getCurrentProduct(state), ['id']);

const getCurrentSizeRecommendation = state => get(getCurrentProduct(state), ['sizeRecommendation']);

const getCurrentMainCarouselSlide = state => get(state, ['pdp', 'currentMainCarouselSlide']);

const getCurrentThumbnailCarouselSlide = state =>
  get(state, ['pdp', 'currentThumbnailCarouselSlide']);

const productUpdatesformModalIsOpen = state =>
  get(state, ['app', 'modal']) === MODAL_TYPES.PDP_UPDATE_FORM;

const colorwayUpdatesFormModalIsOpen = state =>
  get(state, ['app', 'modal']) === MODAL_TYPES.PDP_COLORWAY_UPDATE_FORM;

const pdpStoreInventoryModalIsOpen = state =>
  get(state, ['app', 'modal']) === MODAL_TYPES.PDP_STORE_INVENTORY;

const sizeChartModalIsOpen = (state, identifier) => get(state, ['app', 'modal']) === identifier;

const zoomViewModalIsOpen = state => get(state, ['app', 'modal']) === MODAL_TYPES.PDP_ZOOM_VIEW;

const addToCartModalIsOpen = state => get(state, ['app', 'modal']) === MODAL_TYPES.PDP_ADD_TO_CART;

const getSlides = state => {
  const currentProduct = getCurrentProduct(state);
  const badge = get(currentProduct, ['badge']);
  const showBadgeOnMobile = get(currentProduct, ['showBadgeOnMobile']);
  const lifestyleVideoCampaign = PersonalizationsSelector.getCampaignByName(
    state,
    CAMPAIGN_NAMES.PDP_LIFESTYLE_VIDEO
  );
  const lifestyleVideoVariation = lifestyleVideoCampaign?.variations?.[0];
  const lifestyleVideoData =
    lifestyleVideoVariation?.payload?.data?.items?.length &&
    lifestyleVideoVariation?.payload?.data?.items?.find(
      ({ masterName }) => masterName === currentProduct.masterName
    );

  const images = get(currentProduct, 'images');
  const videoUrl = get(currentProduct, 'videoUrl');
  const lifestyleImage = {
    type: MEDIA_TYPES.IMAGE,
    badge: null,
    ...get(currentProduct, 'galleryLifestyleImage', {}),
  };

  const imageSlides = map(images, ({ src, alt }, index) => ({
    src,
    alt,
    type: MEDIA_TYPES.IMAGE,
    badge: index === 0 ? badge : null,
    showBadgeOnMobile,
  }));

  const slides = [...imageSlides];

  if (videoUrl) {
    const experimentLifestyleVideoUrl = lifestyleVideoData?.url;
    const posterUrl = Cloudinary.generateVideoThumbnail(experimentLifestyleVideoUrl || videoUrl);

    const videoSlide = {
      src: experimentLifestyleVideoUrl || videoUrl,
      alt: 'Video',
      poster: posterUrl,
      type: MEDIA_TYPES.VIDEO,
    };

    // Put video so that it's at the end of the first slide of thumbnails
    if (slides.length < N_THUMBNAILS_PER_SLIDE) {
      slides.push(videoSlide);
    } else {
      slides.splice(N_THUMBNAILS_PER_SLIDE - 1, 0, videoSlide);
    }
  }

  if (lifestyleImage.src) {
    const contentfulImages = get(currentProduct, 'contentfulImages', []);
    const duplicateIndex = contentfulImages.findIndex(({ src }) => {
      const fileName = src.split('/').pop();
      return fileName === lifestyleImage.src.split('/').pop();
    });

    // Remove duplicate image from images coming from Colorway
    if (duplicateIndex > -1) {
      slides.splice(duplicateIndex, 1);
    }

    // Put lifestyle image as third slide
    slides.splice(2, 0, lifestyleImage);
  }

  return slides;
};

const getContentfulPoweredSlides = state => {
  const currentProduct = getCurrentProduct(state);
  const shopifyImages = get(currentProduct, 'images');
  const images = get(currentProduct, 'contentfulImages');
  const videoUrl = get(currentProduct, 'videoUrl');
  const imagesAltText = map(shopifyImages, ({ alt }) => alt);
  const imageSlides = map(images, ({ src, alt, tags }, index) => ({
    src,
    alt: alt || imagesAltText[index],
    tags,
    type: MEDIA_TYPES.IMAGE,
  }));
  const slides = [...imageSlides];

  if (videoUrl) {
    const videoSlide = {
      src: videoUrl,
      alt: 'Video',
      poster: Cloudinary.generateVideoThumbnail(videoUrl),
      type: MEDIA_TYPES.VIDEO,
    };

    // Put video so that it's at the end of the first slide of thumbnails
    if (slides.length < N_THUMBNAILS_PER_SLIDE) {
      slides.push(videoSlide);
    } else {
      slides.splice(N_THUMBNAILS_PER_SLIDE - 1, 0, videoSlide);
    }
  }

  return slides;
};

const getProductModelDescription = state => {
  const currentProduct = getCurrentProduct(state);
  return get(currentProduct, 'modelDescription', null);
};

const getVideoModalUrl = state => get(state, ['pdp', 'videoModalUrl']);

const priceTierSearchParams = () => new URL(window.location).searchParams.get('price-tiers');

const priceTierOverrides = () =>
  isEmpty(priceTierSearchParams()) ? [] : split(priceTierSearchParams(), ',');

const isSalePdp = () => {
  if (isEmpty(priceTierOverrides())) {
    return false;
  }

  return !includes(priceTierOverrides(), MSRP_PRICE_TIER);
};

const filterProductsBySaleStatus = memoize((products, priceTiers) => {
  if (isEmpty(priceTiers)) {
    return products;
  } else {
    return filter(
      products,
      product => product.priceTier && includes(priceTiers, product.priceTier)
    );
  }
}, cacheKey);

const getSaleFilteredProducts = state =>
  filterProductsBySaleStatus(getAllProducts(state), priceTierOverrides());

const filterProductsByEdition = memoize(
  products =>
    filter(products, ({ edition, colorName }) => {
      if (!edition) {
        Log.error(new Error(`${colorName} does not have an edition specified.`));
      }
      return edition;
    }),
  cacheKey
);

const filterProductsByPrice = memoize(
  (products, initialProduct) =>
    filter(products, ({ price, isAvailable, handle }) => {
      return price && (isAvailable || handle === initialProduct);
    }),
  cacheKey
);

const sortProductsByPrice = memoize(products => sortBy(products, 'price'), cacheKey);

const getProductsByPrice = state =>
  sortProductsByPrice(
    filterProductsByPrice(getSaleFilteredProducts(state), getInitialProductHandle(state))
  );

const sortProductsByEdition = memoize(
  products => sortBy(products, ({ edition }) => EDITIONS_PRIORITY[edition]),
  cacheKey
);

const getProductsByEdition = state =>
  sortProductsByEdition(filterProductsByEdition(getSaleFilteredProducts(state)));

const getProductsGroupedByEdition = state => groupBy(getProductsByEdition(state), 'edition');

const getProductsGroupedByPrice = state => groupBy(getProductsByPrice(state), 'formattedPrice');

const getExperimentImagesLoading = state => {
  const shouldWaitForExperiment = Settings.shopID === SHOP_IDS.US;
  const testImagesDataCampaign = PersonalizationsSelector.getCampaignByName(
    state,
    CAMPAIGN_NAMES.PDP_IMAGES_EXPERIMENT
  );
  return (
    shouldWaitForExperiment &&
    (!testImagesDataCampaign?.status ||
      testImagesDataCampaign?.status === PERSONALIZATION_STATUS.LOADING)
  );
};

const getExperimentImagesPdp = (state, testImagesDataCampaign) => {
  if (testImagesDataCampaign?.status === PERSONALIZATION_STATUS.SUCCESS) {
    const { isExperimentActive, dynamicYieldIdentifiers } = new BooleanExperimentCampaign({
      campaign: testImagesDataCampaign,
      experimentIdentifier: CAMPAIGN_IDENTIFIERS.PDP_TEST_IMAGES,
    });
    const { dyExperiments } = getCurrentProduct(state);
    const dyTestImageData =
      dyExperiments &&
      dyExperiments[CAMPAIGN_NAMES.PDP_IMAGES_EXPERIMENT] &&
      isExperimentActive == 'true' &&
      dyExperiments[CAMPAIGN_NAMES.PDP_IMAGES_EXPERIMENT][dynamicYieldIdentifiers?.variationId];
    return dyTestImageData;
  }
};

export {
  getCurrentProduct,
  getCurrentOpenAttributeIndex,
  getCurrentSize,
  getCurrentProductHandle,
  getAllProducts,
  isSalePdp,
  priceTierOverrides,
  getSaleFilteredProducts,
  getIsLoadingMasterProduct,
  getCurrentVariant,
  getCurrentSku,
  getCurrentId,
  getVideoModalUrl,
  productUpdatesformModalIsOpen,
  colorwayUpdatesFormModalIsOpen,
  getCurrentMainCarouselSlide,
  getCurrentThumbnailCarouselSlide,
  sizeChartModalIsOpen,
  zoomViewModalIsOpen,
  getSlides,
  getContentfulPoweredSlides,
  getProductModelDescription,
  getProductsByEdition,
  getProductsGroupedByEdition,
  getProductsGroupedByPrice,
  getCurrentSizeRecommendation,
  getInitialProductHandle,
  getAdditionalPdpModules,
  addToCartModalIsOpen,
  pdpStoreInventoryModalIsOpen,
  getExperimentImagesPdp,
  getExperimentImagesLoading,
  getSelectedQuantity,
};
